import { IS_BROWSER } from '../is-browser';

/**
 * Connectivity check endpoints in order of preference.
 * Using multiple endpoints provides redundancy against single-point failures.
 */
const CONNECTIVITY_ENDPOINTS = [
  'https://1.1.1.1/cdn-cgi/trace', // Cloudflare - fast, lightweight
  'https://dns.google/resolve?name=google.com&type=A', // Google DNS over HTTPS
  'https://www.gstatic.com/generate_204' // Google's connectivity check endpoint
];

/**
 * Attempts to fetch a single endpoint with timeout.
 * @param url - The endpoint URL to check
 * @param timeout - Timeout in milliseconds
 * @returns true if endpoint is reachable, false otherwise
 */
const checkEndpoint = async (url: string, timeout: number): Promise<boolean> => {
  try {
    const fetchPromise = fetch(url, {
      method: 'GET',
      mode: 'no-cors',
      cache: 'no-store'
    });

    const timeoutPromise = new Promise<Response>((_, reject) => {
      setTimeout(() => reject(new Error('Network check timeout')), timeout);
    });

    await Promise.race([fetchPromise, timeoutPromise]);

    return true;
  } catch (error: unknown) {
    return false;
  }
};

/**
 * Checks actual internet connectivity by attempting to reach lightweight endpoints.
 *
 * Note: Tizen's webapis.network.getActiveConnectionType() is unreliable when the router
 * loses internet connection (TV reports as online even when router has no internet).
 * Therefore, we always perform an actual connectivity test via fetch on TV.
 *
 * Implementation uses parallel checks to multiple endpoints for maximum speed and reliability:
 * - Cloudflare (1.1.1.1) - fast, lightweight
 * - Google DNS over HTTPS - very reliable
 * - Google's connectivity check endpoint - industry standard
 *
 * Returns true if ANY endpoint is reachable within 3 seconds, providing fast results
 * even if one endpoint is slow or blocked.
 *
 * In browser environments (development), we use navigator.onLine for instant checks
 *
 * @returns true if device has internet access, false otherwise
 */
export const checkNetwork = async (): Promise<boolean> => {
  if (IS_BROWSER) {
    return navigator.onLine;
  }

  const checks = CONNECTIVITY_ENDPOINTS.map((endpoint) => checkEndpoint(endpoint, 3000));
  const results = await Promise.all(checks);

  return results.some((result) => result === true);
};

export default checkNetwork;
